import { getLogger } from '@packages/core/logging';

import type { DtcCodeEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';
import { toDtcCodeEntity } from '../helpers';

const logger = getLogger('dtc-codes');

/** Parameters for searching DTC codes. */
export type SearchDtcCodesParams = {
    /** The search text to match against DTC codes. */
    query: string;

    /** Optional abort signal for cancellation. */
    signal?: AbortSignal;
};

/**
 * Search for DTC codes by text query. Returns matching codes or an empty array if the request
 * was aborted or no results were found.
 */
export class SearchDtcCodes extends StsBackendOperation {
    /**
     * Execute the DTC code search.
     *
     * @param params - the search query text and optional abort signal
     * @throws {Error} when the API request fails for a reason other than abort
     * @returns the matching DTC code entities, or an empty array if aborted or no results
     */
    async exec({ query, signal }: SearchDtcCodesParams): Promise<DtcCodeEntity[]> {
        // Fetch matching DTC codes from the API
        try {
            const response = await this.client.fetchDtcCodes({ query }, signal);

            if (!response.success && response.aborted) {
                return [];
            } else if (!response.success) {
                throw new Error('DTC code search failed', { cause: response.data });
            }

            if (!response.data?.dtcCodes) {
                return [];
            }

            // Map DTOs to domain entities
            return response.data.dtcCodes.map(toDtcCodeEntity);
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to get DTC code';
            logger.error('Failed to search the DTC codes', { error: e });
            throw new Error(message);
        }
    }
}
